import _ from "lodash";
import { Contact } from "./Contact";
import { Employe } from "./Employe";
import { Intervention } from "./Intervention";
import { DateUtils } from "../utils/DateUtils";
import moment from "moment";
import { ModificationHistory } from "./ModificationHistory";
import IFrequence = Intervention.IFrequence;

// Class
export class RecurrenceIntervention {
    id?: number;
    private _client?: Contact;
    client_id?: number;
    duree_prevue?: number;
    employes: Array<Employe> = [];
    frequences: Array<RecurrenceIntervention.Frequence> = [];
    horaire_debut?: string;
    horaire_fin?: string;
    titre?: string;
    planning_id?: number;
    start_date_effet: string = moment().format("YYYY-MM-DD");
    end_date_effet: string | null = null;

    rattrapable: boolean = false;
    to_notify: boolean = false;
    archived: boolean = false;
    remplacements: Array<RecurrenceIntervention.Remplacement> = [];
    current_remplacement?: RecurrenceIntervention.Remplacement;
    interventions_secondaires: Array<RecurrenceIntervention.InterventionSecondaire> =
        [];
    history: Array<ModificationHistory> = [];

    constructor(data: any) {
        var self = this;
        _.assign(
            this,
            _.omit(data, [
                "client",
                "employes",
                "frequences",
                "remplacements",
                "current_remplacement",
                "interventions_secondaires",
                "history",
            ])
        );
        if (data.client) {
            self._client = new Contact(data.client);
        }
        _.forEach(data.employes, function (employe) {
            self.employes.push(new Employe(employe));
        });
        _.forEach(data.frequences, function (frequence) {
            self.frequences.push(
                new RecurrenceIntervention.Frequence(frequence)
            );
        });
        _.forEach(data.remplacements, function (remplacement) {
            self.remplacements.push(
                new RecurrenceIntervention.Remplacement(remplacement)
            );
        });
        if (data.current_remplacement) {
            self.current_remplacement = new RecurrenceIntervention.Remplacement(
                data.current_remplacement
            );
        }
        _.forEach(
            data.interventions_secondaires,
            function (intervention_secondaire) {
                self.interventions_secondaires.push(
                    new RecurrenceIntervention.InterventionSecondaire(
                        intervention_secondaire
                    )
                );
            }
        );
        _.forEach(data.history, function (historyLine) {
            self.history.push(new ModificationHistory(historyLine));
        });
        if (self.frequences.length === 0) {
            self.frequences.push(
                new RecurrenceIntervention.Frequence({ is_default: true })
            );
        }
    }

    //
    get client(): Contact | undefined {
        return this._client;
    }

    set client(value: Contact | undefined) {
        if (value) {
            this._client = value;
            this.client_id = value.id;
        }
    }

    //
    get employes_ids(): Array<number> {
        // @ts-ignore
        return _.map(this.employes, "id");
    }

    //
    get start_date_effet_str(): string {
        return DateUtils.sqlToDateStr(this.start_date_effet);
    }

    set start_date_effet_str(date: string) {
        var newDate = DateUtils.dateStrToSql(date);

        if (!_.isEmpty(newDate)) {
            this.start_date_effet = newDate;
        }
    }

    //
    get end_date_effet_str(): string {
        return DateUtils.sqlToDateStr(this.end_date_effet);
    }

    set end_date_effet_str(date: string) {
        var newDate = DateUtils.dateStrToSql(date);

        if (_.isEmpty(newDate)) {
            this.end_date_effet = null;
        } else {
            this.end_date_effet = newDate;
        }
    }

    //
    get summary(): string {
        let res = "";

        if (this.client) {
            res = this.client.nom + " - ";
        }

        res += this.titre;

        return _.truncate(res, {
            length: 65,
            separator: /,? +/,
        });
    }

    get default_frequence(): RecurrenceIntervention.Frequence {
        return _.find(this.frequences, { is_default: true });
    }

    // Utils
    updateHoraireFin() {
        if (this.horaire_debut && this.duree_prevue) {
            let date = moment(this.horaire_debut, "HH[h]mm");
            date.add(this.duree_prevue, "h");
            this.horaire_fin = date.format("HH[h]mm");
        }
    }

    serialize(): RecurrenceIntervention.IRecurrenceIntervention {
        var res = {
            id: this.id,
            client_id: this.client_id,
            duree_prevue: this.duree_prevue,
            employes_ids: this.employes_ids,
            horaire_debut: this.horaire_debut,
            horaire_fin: this.horaire_fin,
            titre: this.titre,
            planning_id: this.planning_id,
            start_date_effet: this.start_date_effet,
            end_date_effet: this.end_date_effet,
            interventions_secondaires: _.map(
                this.interventions_secondaires,
                function (interventionSecondaire) {
                    return interventionSecondaire.serialize();
                }
            ),
            rattrapable: this.rattrapable,
            to_notify: this.to_notify,
            archived: this.archived,
        };

        return res;
    }
}

// Enums
export namespace RecurrenceIntervention {
    export interface IRecurrenceIntervention {
        id?: number;
        client_id?: number;
        employes_ids: Array<number>;
        horaire_debut?: string;
        horaire_fin?: string;
        titre?: string;
        start_date_effet: string;
        end_date_effet: string | null;
        rattrapable: boolean;
        to_notify: boolean;
    }

    export interface IRecurrenceInterventionFrequence {
        id?: number;
        date_debut?: string;
        date_fin?: string;
        frequence: Intervention.EFrequence;
        is_default: boolean;
        jours: Array<IRecurrenceInterventionFrequenceJour>;
    }

    export interface IRecurrenceInterventionFrequenceJour {
        id?: number;
        employes_ids: Array<number>;
        horaire_debut?: string;
        horaire_fin?: string;
        jour?: DateUtils.EWeekDays;
        last_generated?: string;
        mois?: DateUtils.EMonths;
        next_date?: string;
        occurence?: number;
    }

    export interface IRemplacement {
        id?: number;
        start_date: string;
        end_date?: string;
        replaced_employe_id?: number;
        replacing_employe_id?: number;
        recurrence_interventions_ids: Array<number>;
    }

    export interface IInterventionSecondaire {
        id?: number;
        description: string;
        client_id?: number;
    }

    export class Frequence {
        id?: number;
        date_debut?: string;
        date_fin?: string;
        frequence: Intervention.EFrequence = Intervention.EFrequence.None;
        is_default: boolean = false;
        jours: Array<FrequenceJour> = [];
        __uniqueid = _.uniqueId();

        constructor(data: any) {
            var self = this;
            _.assign(this, _.omit(data, ["jours"]));

            _.forEach(data.jours, function (jour) {
                self.jours.push(new FrequenceJour(jour));
            });
        }

        //
        get date_debut_str(): string {
            return DateUtils.sqlToDateStr(this.date_debut, "DD/MM");
        }

        set date_debut_str(date: string) {
            var newDate = DateUtils.dateStrToSql(date, "DD/MM");

            if (!_.isEmpty(newDate)) {
                this.date_debut = newDate;
            }
        }

        //
        get date_fin_str(): string {
            return DateUtils.sqlToDateStr(this.date_fin, "DD/MM");
        }

        set date_fin_str(date: string) {
            var newDate = DateUtils.dateStrToSql(date, "DD/MM");

            if (!_.isEmpty(newDate)) {
                this.date_fin = newDate;
            }
        }

        // Utils
        serialize(): RecurrenceIntervention.IRecurrenceInterventionFrequence {
            var res = {
                id: this.id,
                date_debut: this.date_debut,
                date_fin: this.date_fin,
                frequence: this.frequence,
                is_default: this.is_default,
                jours: _.map(this.jours, function (jour) {
                    return jour.serialize();
                }),
            };

            return res;
        }

        isWeekly(): boolean {
            return (
                this.frequence == Intervention.EFrequence.Hebdomadaire ||
                this.frequence ==
                    Intervention.EFrequence.ToutesLesDeuxSemaines ||
                this.frequence == Intervention.EFrequence.DuLundiAuSamedi ||
                this.frequence == Intervention.EFrequence.DuLundiAuVendredi
            );
        }

        isSemiweekly(): boolean {
            return (
                this.frequence == Intervention.EFrequence.ToutesLesDeuxSemaines
            );
        }

        isMonthly(): boolean {
            return (
                this.frequence == Intervention.EFrequence.Bimensuel ||
                this.frequence == Intervention.EFrequence.Mensuel ||
                this.frequence == Intervention.EFrequence.Bimestriel ||
                this.frequence == Intervention.EFrequence.Trimestriel ||
                this.frequence == Intervention.EFrequence.Semestriel ||
                this.frequence == Intervention.EFrequence.Annuel
            );
        }

        isYearly(): boolean {
            return (
                this.frequence == Intervention.EFrequence.Bimestriel ||
                this.frequence == Intervention.EFrequence.Trimestriel ||
                this.frequence == Intervention.EFrequence.Semestriel ||
                this.frequence == Intervention.EFrequence.Annuel
            );
        }
    }

    export class FrequenceJour {
        id?: number;
        employes: Array<Employe> = [];
        horaire_debut?: string;
        horaire_fin?: string;
        jour?: DateUtils.EWeekDays;
        mois?: DateUtils.EMonths;
        occurrence?: number;
        __uniqueid = _.uniqueId();

        constructor(data: any) {
            var self = this;
            _.assign(this, _.omit(data, ["employes"]));
            if (data.occurence) {
                this.occurrence = data.occurence; // TODO fix typo in api
            }

            _.forEach(data.employes, function (employe) {
                self.employes.push(new Employe(employe));
            });
        }

        //
        get employes_ids(): Array<number> {
            // @ts-ignore
            return _.map(this.employes, "id");
        }

        // Utils
        serialize(): RecurrenceIntervention.IRecurrenceInterventionFrequenceJour {
            var res = {
                id: this.id,
                employes_ids: this.employes_ids,
                horaire_debut: this.horaire_debut,
                horaire_fin: this.horaire_fin,
                jour: this.jour,
                mois: this.mois,
                occurence: this.occurrence,
            };

            return res;
        }
    }

    export class Remplacement {
        id?: number;
        start_date: string = moment().format("YYYY-MM-DD");
        end_date?: string;
        replaced_employe?: Employe;
        replacing_employe?: Employe;
        recurrence_interventions: Array<RecurrenceIntervention> = [];
        recurrence_interventions_count: number = 0;
        __recurrence_interventions_ids: Array<number> = [];

        constructor(data: any) {
            var self = this;
            _.assign(
                this,
                _.omit(data, [
                    "replaced_employe",
                    "replacing_employe",
                    "recurrence_interventions",
                ])
            );
            if (data.replaced_employe) {
                self.replaced_employe = new Employe(data.replaced_employe);
            }
            if (data.replacing_employe) {
                self.replacing_employe = new Employe(data.replacing_employe);
            }
            _.forEach(
                data.recurrence_interventions,
                function (recurrence_intervention) {
                    self.recurrence_interventions.push(
                        new RecurrenceIntervention(recurrence_intervention)
                    );
                }
            );
        }

        //
        get start_date_str(): string {
            return DateUtils.sqlToDateStr(this.start_date);
        }

        set start_date_str(date: string) {
            var newDate = DateUtils.dateStrToSql(date);

            if (!_.isEmpty(newDate)) {
                this.start_date = newDate;
            }
        }

        //
        get end_date_str(): string {
            return DateUtils.sqlToDateStr(this.end_date);
        }

        set end_date_str(date: string) {
            var newDate = DateUtils.dateStrToSql(date);

            if (!_.isEmpty(newDate)) {
                this.end_date = newDate;
            } else {
                this.end_date = null;
            }
        }

        //
        get recurrence_interventions_ids(): Array<number> {
            if (this.recurrence_interventions.length > 0) {
                // @ts-ignore
                return _.chain(this.recurrence_interventions)
                    .map("id")
                    .uniq()
                    .value();
            } else {
                return this.__recurrence_interventions_ids;
            }
        }

        set recurrence_interventions_ids(value: Array<number>) {
            this.__recurrence_interventions_ids = value;
        }

        // Utils
        serialize(): RecurrenceIntervention.IRemplacement {
            var res = {
                id: this.id,
                start_date: this.start_date,
                end_date: this.end_date,
                replaced_employe_id: this.replaced_employe
                    ? this.replaced_employe.id
                    : undefined,
                replacing_employe_id: this.replacing_employe
                    ? this.replacing_employe.id
                    : undefined,
                recurrence_interventions_ids: this.recurrence_interventions_ids,
            };

            return res;
        }

        isValid(): boolean {
            return !!(
                this.start_date &&
                this.replaced_employe &&
                this.replacing_employe &&
                this.dateIsValid()
            );
        }

        dateIsValid(): boolean {
            let today = moment().format("YYYY-MM-DD");
            return !!this.start_date && this.start_date >= today;
        }
    }

    export class InterventionSecondaire {
        id?: number;
        description: string = "";
        client_id?: number;
        _client?: Contact;
        __uniqueid = _.uniqueId();

        constructor(data: any) {
            var self = this;
            _.assign(this, _.omit(data, ["client"]));
            if (data.client) {
                self._client = new Contact(data.client);
            }
        }

        //
        get client(): Contact | undefined {
            return this._client;
        }

        set client(value: Contact | undefined) {
            if (value) {
                this._client = value;
                this.client_id = value.id;
            } else {
                this._client = undefined;
                this.client_id = undefined;
            }
        }

        // Utils
        serialize(): RecurrenceIntervention.IInterventionSecondaire {
            var res = {
                id: this.id,
                description: this.description,
                client_id: this.client_id,
            };

            return res;
        }
    }

    export enum EOccurrenceHebdo {
        odd = 1,
        even = 2,
    }

    export const OccurrenceHebdo = {
        [EOccurrenceHebdo.odd]: {
            id: EOccurrenceHebdo.odd,
            name: "semaines impaires",
        },
        [EOccurrenceHebdo.even]: {
            id: EOccurrenceHebdo.even,
            name: "semaines paires",
        },
    };

    export enum EOccurrence {
        first = 1,
        second = 2,
        third = 3,
        fourth = 4,
        last = 5,
    }

    export const Occurrence = {
        [EOccurrence.first]: { id: EOccurrence.first, name: "premier" },
        [EOccurrence.second]: { id: EOccurrence.second, name: "deuxième" },
        [EOccurrence.third]: { id: EOccurrence.third, name: "troisième" },
        [EOccurrence.fourth]: { id: EOccurrence.fourth, name: "quatrième" },
        [EOccurrence.last]: { id: EOccurrence.last, name: "dernier" },
    };
}
